// Mitigation.jsx — Strategic recommendations + action-plan tracker
import React, { useEffect, useState } from "react";
import {
  Box, Paper, Button, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Tooltip, Alert,
} from "@mui/material";
import {
  Lightbulb, Plus, Trash2, Wand2, CheckCircle2, Clock, AlertTriangle, ChevronRight,
} from "lucide-react";
import { toast } from "react-toastify";
import DashboardLayout from "../layouts/DashboardLayout";
import ScenarioToggle from "../components/ScenarioToggle";
import { recoApi, actionPlansApi } from "../api/extra";
import { formatApiError } from "../api/api";
import { fmtDateTime } from "../utils/format";

const PRIORITY_COLOR = { P0: "#A93226", P1: "#8F5E14", P2: "#2E5A35" };
const STATUS_COLOR = {
  planned: "#6B7280", in_progress: "#1E2A38", completed: "#2E5A35",
  overdue: "#A93226", dropped: "#94A3B8",
};
const STATUSES = ["planned", "in_progress", "completed", "overdue", "dropped"];

export default function Mitigation() {
  const [tab, setTab] = useState(0);
  const [scenario, setScenario] = useState("current");
  const [portfolioRecos, setPortfolioRecos] = useState([]);
  const [opsRecos, setOpsRecos] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", target_type: "portfolio", priority: "P1", status: "planned", owner_dept: "", due_date: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [p, o, pl] = await Promise.all([
        recoApi.portfolio(scenario),
        recoApi.operations(scenario),
        actionPlansApi.list({}),
      ]);
      setPortfolioRecos(p.data.recommendations || []);
      setOpsRecos(o.data.recommendations || []);
      setPlans(pl.data.action_plans || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scenario]);

  const autogen = async (target_type) => {
    setBusy(true);
    try {
      const { data } = await actionPlansApi.generate(target_type, scenario);
      toast.success(`${data.created} new action plans created (${data.total_recommendations} total recommendations)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const adoptOne = async (strategy) => {
    try {
      await actionPlansApi.create({
        title: strategy.title, strategy_id: strategy.strategy_id,
        target_type: strategy.applies_to?.includes("facility") ? "operations" : "portfolio",
        owner_dept: strategy.owner_dept, priority: strategy.priority,
        status: "planned", notes: strategy.rationale || "",
      });
      toast.success("Adopted into action plan");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateStatus = async (plan, status) => {
    try {
      await actionPlansApi.update(plan.plan_id, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removePlan = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.title}"?`)) return;
    try { await actionPlansApi.remove(plan.plan_id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const createCustom = async () => {
    try {
      await actionPlansApi.create(form);
      setOpen(false); setForm({ title: "", target_type: "portfolio", priority: "P1", status: "planned", owner_dept: "", due_date: "", notes: "" });
      toast.success("Action plan added");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const stats = {
    planned: plans.filter((p) => p.status === "planned").length,
    in_progress: plans.filter((p) => p.status === "in_progress").length,
    completed: plans.filter((p) => p.status === "completed").length,
    overdue: plans.filter((p) => p.status === "overdue").length,
  };

  return (
    <DashboardLayout
      title="Mitigation & Action Plans"
      subtitle="STRATEGIC RECOMMENDATIONS · OWNER · STATUS"
      headerExtra={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <ScenarioToggle value={scenario} onChange={setScenario} />
          <Button size="small" variant="contained" onClick={() => setOpen(true)} startIcon={<Plus size={13} />}
            data-testid="add-plan-btn"
            sx={{ background: "#1E2A38", "&:hover": { background: "#0F1721" } }}>
            Add plan
          </Button>
        </Box>
      }
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        Strategies below are auto-generated from the current portfolio + operations risk profile.
        Adopt them as tracked action plans (with owner department + due date) or auto-generate the full set.
      </Alert>

      {/* Stats */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1.6, mb: 2 }}>
        <StatCard label="Planned" value={stats.planned} icon={<Clock size={15} />} color="#6B7280" testid="plan-stat-planned" />
        <StatCard label="In Progress" value={stats.in_progress} icon={<Wand2 size={15} />} color="#1E2A38" testid="plan-stat-progress" />
        <StatCard label="Completed" value={stats.completed} icon={<CheckCircle2 size={15} />} color="#2E5A35" testid="plan-stat-done" />
        <StatCard label="Overdue" value={stats.overdue} icon={<AlertTriangle size={15} />} color="#A93226" testid="plan-stat-overdue" />
      </Box>

      <Paper sx={{ overflow: "hidden", mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: "1px solid #E2E8F0", px: 2 }}>
          <Tab label="Portfolio Strategies" data-testid="tab-portfolio" />
          <Tab label="Operations Strategies" data-testid="tab-operations" />
          <Tab label={`Active Plans (${plans.length})`} data-testid="tab-plans" />
        </Tabs>

        {loading ? <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box> : (
          <>
            {tab === 0 && (
              <RecoTable recos={portfolioRecos} onAdopt={adoptOne} onAutogen={() => autogen("portfolio")} busy={busy}
                title="Portfolio-level recommendations" testid="reco-portfolio-table" />
            )}
            {tab === 1 && (
              <RecoTable recos={opsRecos} onAdopt={adoptOne} onAutogen={() => autogen("operations")} busy={busy}
                title="Operations / Bank-own-footprint recommendations" testid="reco-operations-table" />
            )}
            {tab === 2 && (
              <Box sx={{ p: 0 }}>
                {plans.length === 0 ? (
                  <Box sx={{ p: 6, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
                    No active action plans. Adopt recommendations from the other tabs, or click "Auto-generate".
                  </Box>
                ) : (
                  <Table size="small" data-testid="plans-table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Plan</TableCell>
                        <TableCell>Target</TableCell>
                        <TableCell>Owner</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {plans.map((p) => (
                        <TableRow key={p.plan_id} hover data-testid={`plan-row-${p.plan_id}`}>
                          <TableCell>
                            <Box sx={{ fontWeight: 600, fontSize: 13 }}>{p.title}</Box>
                            {p.strategy_id && (
                              <Box className="cb-mono" sx={{ fontSize: 10.5, color: "#6B7280" }}>{p.strategy_id}</Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>{p.target_type}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{p.owner_dept || "—"}</TableCell>
                          <TableCell>
                            <Chip size="small" label={p.priority}
                              sx={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10, fontWeight: 700,
                                    background: PRIORITY_COLOR[p.priority] + "22", color: PRIORITY_COLOR[p.priority] }} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" select value={p.status}
                              onChange={(e) => updateStatus(p, e.target.value)}
                              SelectProps={{ inputProps: { "data-testid": `plan-status-${p.plan_id}` } }}
                              sx={{ minWidth: 130, "& .MuiOutlinedInput-root": { fontSize: 12 } }}>
                              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace("_", " ")}</MenuItem>)}
                            </TextField>
                          </TableCell>
                          <TableCell sx={{ fontSize: 11.5 }}>{fmtDateTime(p.created_at)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => removePlan(p)}
                                data-testid={`plan-del-${p.plan_id}`}>
                                <Trash2 size={13} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Add plan dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "Playfair Display, serif", fontWeight: 600 }}>Add custom action plan</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 1.6, mt: 1 }}>
            <TextField size="small" required label="Title" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} inputProps={{ "data-testid": "pf-title" }} />
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
              <TextField size="small" select label="Target type" value={form.target_type}
                onChange={(e) => setForm({ ...form, target_type: e.target.value })}>
                {["portfolio", "operations", "collateral", "facility", "borrower"].map((t) =>
                  <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField size="small" select label="Priority" value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                {["P0", "P1", "P2"].map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
              <TextField size="small" label="Owner department" value={form.owner_dept}
                onChange={(e) => setForm({ ...form, owner_dept: e.target.value })} />
              <TextField size="small" type="date" label="Due date" value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Box>
            <TextField size="small" multiline minRows={2} label="Notes" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createCustom} disabled={!form.title}
            data-testid="pf-save"
            sx={{ background: "#1E2A38" }}>Save</Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

function StatCard({ label, value, icon, color, testid }) {
  return (
    <Paper sx={{ p: 1.8, borderLeft: `3px solid ${color}` }} data-testid={testid}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box className="cb-kpi-label">{label}</Box>
        <Box sx={{ color }}>{icon}</Box>
      </Box>
      <Box className="cb-mono" sx={{ fontSize: 26, fontWeight: 600, mt: 0.4 }}>{value}</Box>
    </Paper>
  );
}

function RecoTable({ recos, onAdopt, onAutogen, busy, title, testid }) {
  return (
    <Box>
      <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #F0EFEA" }}>
        <Box>
          <Box className="cb-display" sx={{ fontSize: 15, fontWeight: 600 }}>{title}</Box>
          <Box sx={{ fontSize: 11.5, color: "#6B7280" }}>{recos.length} recommendation(s) triggered by current portfolio state.</Box>
        </Box>
        <Button size="small" variant="contained" onClick={onAutogen} disabled={busy}
          startIcon={busy ? <CircularProgress size={12} sx={{ color: "#FFF" }} /> : <Wand2 size={13} />}
          data-testid="autogen-btn"
          sx={{ background: "#2E5A35", "&:hover": { background: "#1f4023" } }}>
          {busy ? "…" : "Auto-generate plans"}
        </Button>
      </Box>
      {recos.length === 0 ? (
        <Box sx={{ p: 6, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
          No recommendations for the current scenario — portfolio appears healthy.
        </Box>
      ) : (
        <Table size="small" data-testid={testid}>
          <TableHead>
            <TableRow>
              <TableCell>Strategy</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Effort / Impact</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recos.map((r) => (
              <TableRow key={r.strategy_id} hover data-testid={`reco-row-${r.strategy_id}`}>
                <TableCell>
                  <Box sx={{ fontWeight: 600, fontSize: 13 }}>{r.title}</Box>
                  <Box className="cb-mono" sx={{ fontSize: 10.5, color: "#6B7280", letterSpacing: "0.04em" }}>{r.strategy_id}</Box>
                  {r.rationale && (
                    <Box sx={{ fontSize: 11.5, color: "#4B5563", mt: 0.4, maxWidth: 480, lineHeight: 1.45 }}>{r.rationale}</Box>
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={r.category} sx={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10 }} />
                </TableCell>
                <TableCell className="cb-mono" sx={{ fontSize: 11.5 }}>
                  Effort: <b>{r.effort}</b><br />
                  Impact: <b>{r.impact}</b><br />
                  Cost: <b>{r.cost_band}</b>
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.owner_dept}</TableCell>
                <TableCell>
                  <Chip size="small" label={r.priority}
                    sx={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10, fontWeight: 700,
                          background: PRIORITY_COLOR[r.priority] + "22", color: PRIORITY_COLOR[r.priority] }} />
                </TableCell>
                <TableCell align="right">
                  <Button size="small" variant="outlined" onClick={() => onAdopt(r)}
                    endIcon={<ChevronRight size={12} />}
                    data-testid={`adopt-${r.strategy_id}`}>
                    Adopt
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
